import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { GithubPicker } from 'react-color';
import {clearRequiredFieldError} from "../../utils/project-utils.js";
import {useRequiredFieldError} from "../../hooks/use-required-field-error.js";

const ColorButton = ({ size = {height: "18px", width: "18px"}, color, onChange, required = false, ariaLabel, openOnFocus = false, buttonRef: externalButtonRef = null }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [pickerAlignment, setPickerAlignment] = useState('left');
    const pickerRef = useRef(null);
    const buttonRef = useRef(null);
    const pointerInteractionRef = useRef(false);
    const requiredFieldInvalid = useRequiredFieldError(buttonRef, required);
    const hasRequiredError = required && !color && requiredFieldInvalid;

    useEffect(() => {
        if (required && color) clearRequiredFieldError(buttonRef.current);
    }, [color]);

    const togglePicker = () => setIsOpen(previous => !previous);

    const handleButtonFocus = () => {
        if (openOnFocus && !pointerInteractionRef.current) setIsOpen(true);
    };

    const handleButtonPointerDown = () => {
        pointerInteractionRef.current = true;
    };

    const handleButtonPointerEnd = () => {
        pointerInteractionRef.current = false;
    };

    const handleChange = (selectedColor) => {
        onChange(selectedColor.hex);
        setIsOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                pickerRef.current &&
                !pickerRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    useLayoutEffect(() => {
        if (!isOpen) return undefined;

        const updatePickerAlignment = () => {
            const button = buttonRef.current;
            const picker = pickerRef.current;

            if (!button || !picker) return;

            const buttonRect = button.getBoundingClientRect();
            const exceedsViewport = buttonRect.left + picker.offsetWidth > window.innerWidth;

            setPickerAlignment(exceedsViewport ? 'right' : 'left');
        };

        updatePickerAlignment();
        window.addEventListener('resize', updatePickerAlignment);

        return () => window.removeEventListener('resize', updatePickerAlignment);
    }, [isOpen]);

    const colors = [
        '#e91e63','#9c27b0','#673ab7','#3f51b5',
        '#2196f3', '#03a9f4', '#00bcd4','#009688',
        '#4caf50','#8bc34a', '#cddc39','#ffeb3b',
        '#ffc107','#ff9800','#ff5722','#f44336'
    ];

    return (
        <div style={{ ...size, position: 'relative', display: 'inline-block', padding: 0, margin: 0 }}>
            <button
                type="button"
                ref={(element) => {
                    buttonRef.current = element;
                    if (typeof externalButtonRef === "function") externalButtonRef(element);
                    else if (externalButtonRef) externalButtonRef.current = element;
                }}
                onPointerDown={handleButtonPointerDown}
                onPointerUp={handleButtonPointerEnd}
                onPointerCancel={handleButtonPointerEnd}
                onFocus={handleButtonFocus}
                onClick={togglePicker}
                aria-label={ariaLabel}
                aria-expanded={isOpen}
                data-required-field={required || undefined}
                data-required-has-value={required ? (color ? "true" : "false") : undefined}
                style={{
                    ...size,
                    backgroundColor: color,
                    border: hasRequiredError ? '2px solid #dc2626' : '1px solid #ccc',
                    transition: 'border-color 150ms ease-out, border-width 150ms ease-out',
                    cursor: 'pointer',
                    borderRadius: '4px',
                }}
            />

            {isOpen && (
                <div
                    ref={pickerRef}
                    style={{
                        position: 'absolute',
                        zIndex: 100,
                        left: pickerAlignment === 'left' ? 0 : 'auto',
                        right: pickerAlignment === 'right' ? 0 : 'auto',
                        transform: pickerAlignment === 'left' ? 'translate(-8px, 4px)' : 'translate(8px, 4px)',
                    }}
                >
                    <GithubPicker
                        color={color}
                        onChangeComplete={handleChange}
                        colors={colors}
                        width={112}
                        triangle={pickerAlignment === 'left' ? 'top-left' : 'top-right'}
                    />
                </div>
            )}
        </div>
    );
};

export default ColorButton;